import { RIPPLING } from '@/integrations/modules/rippling/consts';
import { getRipplingWorkers } from '@/integrations/modules/rippling/selectors/getEmployees';
import {
  getAccountJobDimensionId,
  getAppListingId,
  getRipplingDimensions,
  getRipplingPositions,
} from '@/integrations/modules/rippling/selectors/getPositions';
import {
  getMatchedRipplingRequestTypes,
  getRipplingRequestTypes,
} from '@/integrations/modules/rippling/selectors/getRequestTypes';
import type { DraftMappings, RipplingData } from '@/integrations/modules/rippling/types';
import Integrations from '@/shared/api/integrations';
import type { WiwState } from '@/store';

import { createAsyncThunk } from '@reduxjs/toolkit';

export interface FetchRipplingConfigurationResponse {
  data: RipplingData;
}

export interface UpdateRippingConfigurationResponse {
  data: {
    job_id?: string;
  };
}

export const fetchRipplingConfiguration = createAsyncThunk(
  'rippling/fetchConfiguration',
  async (_, { rejectWithValue }) => {
    try {
      const response = await Integrations.get(`/${RIPPLING}/v2/config`);

      return response as FetchRipplingConfigurationResponse;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchRipplingConfigurationIfNeeded = createAsyncThunk(
  'rippling/fetchConfigurationIfNeeded',
  async (_, { dispatch, getState }) => {
    const state = getState() as WiwState;
    if (!state.integrations.rippling.configurationLoaded && !state.integrations.rippling.loading) {
      return dispatch(fetchRipplingConfiguration());
    }
    return Promise.resolve();
  },
);

export const updateRipplingConfiguration = createAsyncThunk(
  'rippling/updateConfiguration',
  async (data: DraftMappings, { rejectWithValue, dispatch }) => {
    try {
      const response = await Integrations.url(`/${RIPPLING}/v2/config`).post(data);
      await dispatch(optimisticallyUpdateRipplingConfiguration(data));

      return response as UpdateRippingConfigurationResponse;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const optimisticallyUpdateRipplingConfiguration = createAsyncThunk(
  'rippling/optimisticallyUpdateConfiguration',
  async (data: DraftMappings, { getState }) => {
    const state = getState() as WiwState;
    const appListingId = getAppListingId(state);
    const accountJobDimensionId = getAccountJobDimensionId(state);
    const ripplingEmployees = getRipplingWorkers(state);
    const ripplingPositions = getRipplingPositions(state);
    const ripplingDimensions = getRipplingDimensions(state);
    const ripplingRequestTypes = getRipplingRequestTypes(state);
    const requestTypesMatched = data.request_types?.matched ?? getMatchedRipplingRequestTypes(state);

    // Add the remote entities and mimic the GET /config payload
    return {
      job_dimension_id: data.job_dimension_id,
      account_job_dimension_id: accountJobDimensionId,
      app_listing_id: appListingId,
      positions: {
        matched: data.positions.matched,
        rippling: ripplingPositions,
        rippling_dimensions: ripplingDimensions,
      },
      users: {
        ignored: data.users.ignored,
        matched: data.users.matched,
        rippling: ripplingEmployees,
      },
      request_types: {
        matched: requestTypesMatched,
        rippling: ripplingRequestTypes,
      },
    };
  },
);
